import { z } from 'zod'
import { BotMode } from '../../enums/bot-mode.enum'
import { BotPeakStrategy } from '../../enums/bot-peak-strategy.enum'
import { BotMaType } from '../../enums/bot-ma-type.enum'
import { refineMinMax } from './bot-refine'
import {
  factorWeightsSchema,
  factorWeightsOverrideSchema,
} from '../../investments/dip-pipeline/dip-factors.schema'

export const createBotInstrumentSchema = z
  .object({
    instrumentUid: z.string().min(1),
    figi: z.string().nullable().optional(),
    ticker: z.string().nullable().optional(),
    instrumentName: z.string().nullable().optional(),
    isEnabled: z.boolean().default(true),
    mode: z.nativeEnum(BotMode).nullable().optional(),
    dropPercentMin: z.number().min(0).max(100).nullable().optional(),
    dropPercentMax: z.number().min(0).max(100).nullable().optional(),
    shiftPercentMin: z.number().min(0).max(100).nullable().optional(),
    shiftPercentMax: z.number().min(0).max(100).nullable().optional(),
    confirmationTicks: z.number().int().min(1).nullable().optional(),
    confirmationSeconds: z.number().int().min(0).nullable().optional(),
    lotsPerStep: z.number().int().min(1).nullable().optional(),
    maxLotsPerBuy: z.number().int().min(1).nullable().optional(),
    shiftChange: z.number().min(0).nullable().optional(),
    lotChange: z.number().min(0).nullable().optional(),
    maxBuyPrice: z.number().min(0).nullable().optional(),
    anchorStaleDays: z.number().int().min(0).max(365).nullable().optional(),
    peakStrategy: z.nativeEnum(BotPeakStrategy).nullable().optional(),
    peakLookbackDays: z.number().int().min(1).max(365).nullable().optional(),
    maType: z.nativeEnum(BotMaType).nullable().optional(),
    factorWeights: factorWeightsOverrideSchema.nullable().optional(),
    desireRate: z.number().nullable().optional(),
    allowBuyAboveTarget: z.boolean().nullable().optional(),
  })
  .superRefine((d, ctx) => {
    refineMinMax(
      ctx,
      d,
      'dropPercentMin',
      'dropPercentMax',
      'dropPercentMin must be <= dropPercentMax'
    )
    refineMinMax(
      ctx,
      d,
      'shiftPercentMin',
      'shiftPercentMax',
      'shiftPercentMin must be <= shiftPercentMax'
    )
  })

export type CreateBotInstrumentDto = z.infer<typeof createBotInstrumentSchema>

export const updateBotInstrumentSchema = z
  .object({
    isEnabled: z.boolean().optional(),
    mode: z.nativeEnum(BotMode).nullable().optional(),
    dropPercentMin: z.number().min(0).max(100).nullable().optional(),
    dropPercentMax: z.number().min(0).max(100).nullable().optional(),
    shiftPercentMin: z.number().min(0).max(100).nullable().optional(),
    shiftPercentMax: z.number().min(0).max(100).nullable().optional(),
    confirmationTicks: z.number().int().min(1).nullable().optional(),
    confirmationSeconds: z.number().int().min(0).nullable().optional(),
    lotsPerStep: z.number().int().min(1).nullable().optional(),
    maxLotsPerBuy: z.number().int().min(1).nullable().optional(),
    shiftChange: z.number().min(0).nullable().optional(),
    lotChange: z.number().min(0).nullable().optional(),
    maxBuyPrice: z.number().min(0).nullable().optional(),
    anchorStaleDays: z.number().int().min(0).max(365).nullable().optional(),
    peakStrategy: z.nativeEnum(BotPeakStrategy).nullable().optional(),
    peakLookbackDays: z.number().int().min(1).max(365).nullable().optional(),
    maType: z.nativeEnum(BotMaType).nullable().optional(),
    factorWeights: factorWeightsOverrideSchema.nullable().optional(),
    desireRate: z.number().nullable().optional(),
    allowBuyAboveTarget: z.boolean().nullable().optional(),
  })
  .superRefine((d, ctx) => {
    refineMinMax(
      ctx,
      d,
      'dropPercentMin',
      'dropPercentMax',
      'dropPercentMin must be <= dropPercentMax'
    )
    refineMinMax(
      ctx,
      d,
      'shiftPercentMin',
      'shiftPercentMax',
      'shiftPercentMin must be <= shiftPercentMax'
    )
  })

export type UpdateBotInstrumentDto = z.infer<typeof updateBotInstrumentSchema>

export const botInstrumentResponseSchema = z.object({
  id: z.string().guid(),
  brokerAccountId: z.string().guid(),
  instrumentUid: z.string(),
  figi: z.string().nullable(),
  ticker: z.string().nullable(),
  instrumentName: z.string().nullable(),
  isEnabled: z.boolean(),
  mode: z.nativeEnum(BotMode).nullable(),
  dropPercentMin: z.number().nullable(),
  dropPercentMax: z.number().nullable(),
  shiftPercentMin: z.number().nullable(),
  shiftPercentMax: z.number().nullable(),
  confirmationTicks: z.number().int().nullable(),
  confirmationSeconds: z.number().int().nullable(),
  lotsPerStep: z.number().int().nullable(),
  maxLotsPerBuy: z.number().int().nullable(),
  shiftChange: z.number().nullable(),
  lotChange: z.number().nullable(),
  maxBuyPrice: z.number().nullable(),
  anchorStaleDays: z.number().int().nullable(),
  peakStrategy: z.nativeEnum(BotPeakStrategy).nullable(),
  peakLookbackDays: z.number().int().nullable(),
  maType: z.nativeEnum(BotMaType).nullable(),
  factorWeights: factorWeightsOverrideSchema.nullable(),
  desireRate: z.number().nullable(),
  allowBuyAboveTarget: z.boolean().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  effectiveConfig: z.object({
    mode: z.nativeEnum(BotMode),
    dropPercentMin: z.number(),
    dropPercentMax: z.number(),
    shiftPercentMin: z.number(),
    shiftPercentMax: z.number(),
    confirmationTicks: z.number().int(),
    confirmationSeconds: z.number().int(),
    lotsPerStep: z.number().int(),
    maxLotsPerBuy: z.number().int(),
    shiftChange: z.number(),
    lotChange: z.number(),
    maxBuyPrice: z.number().nullable(),
    anchorStaleDays: z.number().int(),
    peakStrategy: z.nativeEnum(BotPeakStrategy),
    peakLookbackDays: z.number().int(),
    maType: z.nativeEnum(BotMaType),
    factorWeights: factorWeightsSchema,
    moodRate: z.number(),
    desireRate: z.number(),
    allowBuyAboveTarget: z.boolean(),
  }),
})

export type BotInstrumentResponseDto = z.infer<typeof botInstrumentResponseSchema>
